// Pure helpers for the "My Notes" scratchpad (no DOM, unit-tested): day grouping for the
// stream, render-order sorting for todo items, the client-side search filter, the []
// checklist trigger and the plain-text export. Caps mirror the server's
// (healo_backend/storage/user_notes.go).

import { appDayDiff, appDayKey, withAppTimeZone } from '@/lib/timezone';

export const NOTES_MAX_FILE_BYTES = 25 * 1024 * 1024;

/** Client-side id for a new todo item (server treats ids as opaque, unique per block). */
export function createTodoItemId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `todo-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Groups a createdAt-desc block list into day buckets for the stream's separators.
 * Returns [{ key, label ("Today"/"Yesterday"/weekday), dateLabel ("12 Jun"), blocks }];
 * blocks without a timestamp land in a trailing "Earlier" bucket. Same civil-day rules as
 * groupThreadsByDay (bucket + label by the day IN THE APP ZONE).
 */
export function groupNoteBlocksByDay(blocks, nowMs = Date.now()) {
  const now = new Date(nowMs);
  const groups = [];
  const groupsByKey = new Map();

  for (const block of blocks || []) {
    const t = new Date(block?.createdAt || '').getTime();
    let key = 'earlier';
    let label = 'Earlier';
    let dateLabel = '';
    if (!Number.isNaN(t) && block?.createdAt) {
      const date = new Date(t);
      key = `day:${appDayKey(date)}`;
      dateLabel = date.toLocaleDateString(undefined, withAppTimeZone({ day: 'numeric', month: 'short' }));
      const dayDiff = appDayDiff(now, date);
      if (dayDiff <= 0) label = 'Today';
      else if (dayDiff === 1) label = 'Yesterday';
      else label = date.toLocaleDateString(undefined, withAppTimeZone({ weekday: 'long' }));
    }
    let group = groupsByKey.get(key);
    if (!group) {
      group = { key, label, dateLabel, blocks: [] };
      groupsByKey.set(key, group);
      groups.push(group);
    }
    group.blocks.push(block);
  }
  return groups;
}

/**
 * Render order for a todos block: unchecked items first in stored order, then done items
 * in stored order ("checked to-dos sink to the bottom of their group"). Purely
 * presentational — the stored order is never rewritten, so unchecking restores position.
 */
export function sortTodoItemsForRender(items) {
  const open = [];
  const done = [];
  for (const item of items || []) {
    (item?.done ? done : open).push(item);
  }
  return [...open, ...done];
}

/** Case-insensitive search across note text, todo labels and filenames. */
export function filterBlocks(blocks, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return blocks || [];
  return (blocks || []).filter((block) => {
    if (block?.type === 'note') {
      return String(block.text || '').toLowerCase().includes(q);
    }
    if (block?.type === 'todos') {
      return (block.items || []).some((item) => String(item?.label || '').toLowerCase().includes(q));
    }
    if (block?.type === 'file') {
      return String(block.file?.filename || '').toLowerCase().includes(q);
    }
    return false;
  });
}

/** Open (unchecked) todo count across every todos block — the badge number. */
export function countOpenTodos(blocks) {
  let count = 0;
  for (const block of blocks || []) {
    if (block?.type !== 'todos') continue;
    for (const item of block.items || []) {
      if (!item?.done) count += 1;
    }
  }
  return count;
}

/**
 * The composer's checklist trigger: text whose first line starts with "[]" (before any
 * newline exists) switches the entry to todo mode. Returns { remainder } — what the user
 * typed after the marker — or null.
 */
export function detectTodoTrigger(text) {
  const value = String(text || '');
  if (value.includes('\n')) return null;
  const match = /^\s*\[\]\s?(.*)$/.exec(value);
  if (!match) return null;
  return { remainder: match[1] };
}

/** Plain-text export of the whole stream, grouped by day, newest first. */
export function formatNotesExport(blocks, nowMs = Date.now()) {
  const lines = [];
  for (const group of groupNoteBlocksByDay(blocks, nowMs)) {
    const heading = [group.label, group.dateLabel].filter(Boolean).join(' · ');
    lines.push(`## ${heading}`);
    for (const block of group.blocks) {
      if (block.type === 'note') {
        lines.push(String(block.text || ''));
      } else if (block.type === 'todos') {
        for (const item of sortTodoItemsForRender(block.items)) {
          lines.push(`${item.done ? '[x]' : '[ ]'} ${item.label}`);
        }
      } else if (block.type === 'file') {
        const size = Number(block.file?.size) > 0 ? ` (${formatExportSize(block.file.size)})` : '';
        lines.push(`📎 ${block.file?.filename || 'file'}${size}`);
      }
      lines.push('');
    }
  }
  return lines.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

function formatExportSize(size) {
  if (!Number.isFinite(size) || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
